import { platformById } from "@ondit/publishing";
import {
	Button,
	ChipInput,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	Input,
	ModalDescription,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
import { Search } from "@ondit/ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import type { PublishingPlatform } from "~/core/api/distribution";
import { ApiError, api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useTabParam } from "~/core/hooks";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Hint, Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { PlatformIdentity } from "~/modules/analytics/components/platform-identity";
import { ConnectAccount } from "./connect";

/**
 * What each platform accepts, and the account for the six Ondit sends to.
 *
 * A brand's settings rather than a tab of Distribution: the rules are read
 * once and revisited when a post is refused, and the account is set once —
 * neither is part of publishing an article.
 */
const GROUPS = ["direct", "manual"] as const;

/** The server's `CATEGORIES`, in the order the filter lists them. */
export const CATEGORIES = [
	"search",
	"launch",
	"directory",
	"ai",
	"startup",
	"social",
	"video",
	"blog",
	"qa",
	"community",
	"developer",
	"media",
	"navigation",
	"design",
	"local",
	"review",
	"industry",
	"b2b",
	"wiki",
] as const;

/** The server's `INDUSTRIES`, in the order the filter lists them. */
export const INDUSTRIES = [
	"legal",
	"healthcare",
	"dental",
	"wellness",
	"home_services",
	"real_estate",
	"automotive",
	"restaurants",
	"travel",
	"events",
	"pets",
	"education",
	"agencies",
	"software",
	"manufacturing",
	"ecommerce",
	"finance",
	"recruiting",
	"nonprofit",
	"startups",
	"ai",
	"developers",
	"design",
	"apps",
] as const;

export const TIERS = ["high", "medium", "low"] as const;

/** What a brand can file its own platform under; the filter reads the data instead. */
const LANGUAGES = ["en", "zh", "ja", "ko", "es", "fr", "de", "pt", "ru", "ar", "it", "id", "vi", "th"];

export interface PlatformFilter {
	languages?: string[];
	categories?: string[];
	industries?: string[];
	tiers?: string[];
}

/**
 * A platform with no languages takes any, and one with no industries serves
 * any, so each answers every filter of its kind — Google Business Profile is
 * as much a dentist's as a bakery's.
 */
export function matches(
	p: PublishingPlatform,
	{ languages = [], categories = [], industries = [], tiers = [] }: PlatformFilter,
) {
	const langs = p.languages ?? [];
	const serves = p.industries ?? [];
	return (
		(languages.length === 0 || langs.length === 0 || langs.some((l) => languages.includes(l))) &&
		(categories.length === 0 || (p.categories ?? []).some((c) => categories.includes(c))) &&
		(industries.length === 0 || serves.length === 0 || serves.some((i) => industries.includes(i))) &&
		(tiers.length === 0 || (!!p.value_tier && tiers.includes(p.value_tier)))
	);
}

/**
 * Most valuable first. The list is six hundred platforms long, and in the
 * bundled order a search console sat below forty AI-tool directories.
 */
export function byValue(a: PublishingPlatform, b: PublishingPlatform) {
	return (b.value_score ?? -1) - (a.value_score ?? -1);
}

export function PublishingPlatforms({ entityId }: { entityId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		...read,
	});
	const accounts = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
	});
	const [search, setSearch] = useState("");
	const [languages, setLanguages] = useState<string[]>([]);
	const [categories, setCategories] = useState<string[]>([]);
	const [industries, setIndustries] = useState<string[]>([]);
	const [tiers, setTiers] = useState<string[]>([]);
	// Tabs, because the two lists stacked ran to eight screens, and the six
	// that take an account sat above a reference nobody acts on from here.
	const [group, setGroup] = useTabParam(GROUPS);
	return (
		<Async state={state}>
			{(data) => {
				const shown = data.platforms
					.filter(
						(p) =>
							`${p.name} ${p.domain}`.toLowerCase().includes(search.toLowerCase()) &&
							matches(p, { languages, categories, industries, tiers }),
					)
					.sort(byValue);
				const present = [...new Set(data.platforms.flatMap((p) => p.languages ?? []))].sort();
				// A connected API and an observed unattended browser contract both
				// finish without a person at publish time. Keep assisted transfers and
				// editorial workflows separate: calling those automatic would make a
				// calendar promise the platform cannot keep.
				const automatic = shown.filter((p) => {
					const contract = platformById(p.id);
					return !!(contract?.credential_provider || contract?.unattended);
				});
				const manual = shown.filter((p) => !automatic.includes(p));
				return (
					<Section
						title={t("publishing.heading")}
						lede={t("distribution.rulesHint")}
						action={<AddPlatform entityId={entityId} />}
					>
						<Tabs value={group} onValueChange={setGroup} className="publishing-settings">
							<div className="publishing-toolbar">
								<div className="title-row">
									<TabsList aria-label={t("settings.section.publishing")}>
										<TabsTrigger value="direct">
											{t("publishing.group.direct")} · {automatic.length}
										</TabsTrigger>
										<TabsTrigger value="manual">
											{t("publishing.group.manual")} · {manual.length}
										</TabsTrigger>
									</TabsList>
									<Hint text={t(group === "direct" ? "publishing.group.directHint" : "publishing.group.manualHint")} />
								</div>
								<div className="ml-auto flex flex-wrap items-center gap-(--s-2)">
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.category")}
										multiSelectValues={categories}
										onMultiSelectChange={setCategories}
										aria-label={t("publishing.category")}
										options={CATEGORIES.map((c) => ({ value: c, label: t(`publishing.category.${c}`) }))}
									/>
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.industry")}
										multiSelectValues={industries}
										onMultiSelectChange={setIndustries}
										aria-label={t("publishing.industry")}
										options={INDUSTRIES.map((i) => ({ value: i, label: t(`publishing.industry.${i}`) }))}
									/>
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.value")}
										multiSelectValues={tiers}
										onMultiSelectChange={setTiers}
										aria-label={t("publishing.value")}
										options={TIERS.map((v) => ({ value: v, label: t(`publishing.value.${v}`) }))}
									/>
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.language")}
										multiSelectValues={languages}
										onMultiSelectChange={setLanguages}
										aria-label={t("publishing.language")}
										options={present.map((l) => ({ value: l, label: fmt.language(locale, l) }))}
									/>
									<ChipInput
										className="w-[calc(var(--s-14)*3.5)] max-w-full"
										icon={Search}
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder={t("distribution.search")}
										aria-label={t("distribution.search")}
									/>
								</div>
							</div>
							<TabsContent value="direct">
								<ul className="publishing-platforms">
									{automatic.map((p) => (
										<PlatformRow key={p.id} platform={p}>
											<Account entityId={entityId} platform={p} accounts={accounts.data?.accounts} />
										</PlatformRow>
									))}
								</ul>
							</TabsContent>
							<TabsContent value="manual">
								<ul className="publishing-platforms">
									{manual.map((p) => (
										<PlatformRow key={p.id} platform={p}>
											<span className="hint">
												{(p.publishing_methods ?? ["manual"])
													.filter((m) => m !== "api")
													.map((m) => t(`distribution.method.${m}`))
													.join(" · ") || t("distribution.method.manual")}
											</span>
										</PlatformRow>
									))}
								</ul>
							</TabsContent>
						</Tabs>
					</Section>
				);
			}}
		</Async>
	);
}

const FLAGS = ["images", "video", "links"] as const;

/**
 * The tier, and the score and traffic rank behind it, with what the number
 * does not mean on hover. In a table column the tier stands alone and the
 * figures sit under it, or the column took a third of the row.
 */
export function PlatformValue({ platform: p, stacked = false }: { platform: PublishingPlatform; stacked?: boolean }) {
	const t = useT();
	const { locale } = useI18n();
	if (!p.value_tier || p.value_score == null) return null;
	const tier = t(`publishing.value.${p.value_tier}`);
	const figures = [
		t("publishing.value.score", { score: String(p.value_score) }),
		p.traffic_rank ? t("publishing.rank", { rank: fmt.number(locale, p.traffic_rank) }) : null,
	]
		.filter(Boolean)
		.join(" · ");
	return stacked ? (
		<span title={t("publishing.value.hint")}>
			{tier}
			<span className="faint block">{figures}</span>
		</span>
	) : (
		<li title={t("publishing.value.hint")}>
			{tier} · {figures}
		</li>
	);
}

/**
 * One platform: who, what it takes, what the rules say, and where they came
 * from. The three rules are marks rather than a label over a value: most rows
 * answer "check with platform" for all three, and four columns of that made
 * forty-four platforms eight screens of the same word.
 */
function PlatformRow({ platform: p, children }: { platform: PublishingPlatform; children: React.ReactNode }) {
	const t = useT();
	const { locale } = useI18n();
	const unknown = FLAGS.every((flag) => p[flag] === "check");
	return (
		<li className="publishing-platform">
			<div className="publishing-platform-head">
				<a href={p.url} target="_blank" rel="noopener noreferrer">
					<PlatformIdentity platform={p} />
				</a>
				<span className="hint">
					{[
						p.domain,
						...(p.categories ?? []).map((c) => t(`publishing.category.${c as (typeof CATEGORIES)[number]}`)),
						p.languages?.length
							? p.languages.map((l) => fmt.language(locale, l)).join(" / ")
							: t("publishing.language.any"),
						p.countries?.length ? p.countries.map((c) => fmt.region(locale, c)).join(" / ") : null,
					]
						.filter(Boolean)
						.join(" · ")}
				</span>
				<div className="publishing-platform-action">{children}</div>
			</div>
			{p.summary && <p className="publishing-platform-summary">{pick(locale, p.summary)}</p>}
			{p.fit && <p className="publishing-platform-notes">{t("publishing.fit", { fit: pick(locale, p.fit) })}</p>}
			<ul className="publishing-platform-facts">
				<PlatformValue platform={p} />
				<li>{t(`channels.cadence.${p.cadence ?? "ongoing"}`)}</li>
				<li>{t(`distribution.${p.format}`)}</li>
				{p.cost && p.cost !== "check" && <li>{t(`publishing.cost.${p.cost}`)}</li>}
				{p.link_type && p.link_type !== "check" && <li>{t(`publishing.link.${p.link_type}`)}</li>}
				{p.industries?.length ? (
					<li>{p.industries.map((i) => t(`publishing.industry.${i as (typeof INDUSTRIES)[number]}`)).join(" / ")}</li>
				) : null}
				{unknown ? (
					<li data-state="check">{t("publishing.rulesUnknown")}</li>
				) : (
					FLAGS.map((flag) => (
						<li key={flag} data-state={p[flag]}>
							{t(`distribution.${flag}`)} · {t(`distribution.${p[flag]}`)}
						</li>
					))
				)}
			</ul>
			{p.notes && <p className="publishing-platform-notes">{pick(locale, p.notes)}</p>}
			<div className="publishing-platform-foot">
				{(p.reviewed_at || p.verified_at) && (
					<span>{t("distribution.reviewed", { date: p.reviewed_at ?? p.verified_at ?? "" })}</span>
				)}
				<Popover>
					<PopoverTrigger asChild>
						<Button variant="link" className="h-auto p-0 text-[length:inherit]">
							{t("distribution.sources")}
							<span className="sr-only"> · {p.name}</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent className="platform-sources">
						<strong>
							{p.name} · {t("distribution.sources")}
						</strong>
						{(p.sources?.length ? p.sources : [p.rules_url]).map((url, index) => (
							<a key={url} href={url} target="_blank" rel="noopener noreferrer">
								{index + 1}. {new URL(url).hostname}
							</a>
						))}
						{p.logo_source_url && (
							<a href={p.logo_source_url} target="_blank" rel="noopener noreferrer">
								{t("distribution.logoSource")}
							</a>
						)}
					</PopoverContent>
				</Popover>
			</div>
		</li>
	);
}

function Account({
	entityId,
	platform,
	accounts = [],
}: {
	entityId: string;
	platform: PublishingPlatform;
	accounts?: { id: string; provider_id: string; name: string }[];
}) {
	const t = useT();
	const { editor } = useAccess();
	const contract = platformById(platform.id);
	if (!contract?.credential_provider)
		return contract?.unattended ? <span className="hint">{t("publishing.platform.browserAutomatic")}</span> : null;
	const mine = accounts.filter((a) => a.provider_id === contract.credential_provider);
	return mine.length > 0 ? (
		<span className="hint">
			{t("publishing.platform.account")} · {mine.map((a) => a.name).join(", ")}
		</span>
	) : (
		<ConnectAccount entityId={entityId} platform={contract} size="sm" variant="outline" disabled={!editor} />
	);
}

function AddPlatform({ entityId }: { entityId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const create = useMutation({
		mutationFn: (platform: Parameters<typeof api.addPublishingPlatform>[1]) =>
			api.addPublishingPlatform(entityId, platform),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const [name, setName] = useState("");
	const [url, setUrl] = useState("");
	const [format, setFormat] = useState("article");
	const [cadence, setCadence] = useState<"once" | "ongoing">("ongoing");
	const [languages, setLanguages] = useState<string[]>([]);
	const [categories, setCategories] = useState<string[]>([]);
	const [flags, setFlags] = useState({ images: "check", video: "check", links: "check" });
	const [limit, setLimit] = useState("");
	const [open, setOpen] = useState(false);
	async function add(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		try {
			await create.mutateAsync({
				id: "",
				name,
				url,
				domain: "",
				region: "global",
				languages,
				categories,
				cadence,
				format,
				...flags,
				max_length: limit ? Number(limit) : null,
				rules_url: url,
				verified_at: null,
			});
			setName("");
			setUrl("");
			setLanguages([]);
			setCategories([]);
			setOpen(false);
			toast.success(t("distribution.saved"));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}
	return (
		<>
			<Button variant="default" disabled={!editor} onClick={() => setOpen(true)}>
				{t("distribution.add")}
			</Button>
			<ChipModal open={open} onOpenChange={setOpen} dismissDisabled={busy} srTitle={t("distribution.add")}>
				<ChipModalHeader onClose={() => setOpen(false)} closeAriaLabel={t("common.close")}>
					{t("distribution.add")}
				</ChipModalHeader>
				<ChipModalBody className="px-(--s-8)">
					<ModalDescription>{t("distribution.rulesHint")}</ModalDescription>
					<form id="add-publishing-platform" onSubmit={add}>
						<fieldset className="bare-fieldset stack" disabled={!editor || busy}>
							<label className="field" htmlFor="distribution-platform">
								{t("distribution.platform")}
								<Input
									id="distribution-platform"
									required
									maxLength={100}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</label>
							<label className="field" htmlFor="platform-url">
								URL
								<Input id="platform-url" required type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
							</label>
							<div className="field">
								{t("distribution.format")}
								<ChipSelect
									value={format}
									onChange={setFormat}
									disabled={!editor || busy}
									aria-label={t("distribution.format")}
									options={["article", "social", "video", "listing"].map((v) => ({
										value: v,
										label: t(`distribution.${v}`),
									}))}
								/>
							</div>
							<div className="field">
								{t("channels.cadence")}
								<ChipSelect
									value={cadence}
									onChange={(value) => setCadence(value as "once" | "ongoing")}
									disabled={!editor || busy}
									aria-label={t("channels.cadence")}
									options={(["ongoing", "once"] as const).map((c) => ({
										value: c,
										label: t(`channels.cadence.${c}`),
									}))}
								/>
							</div>
							<div className="field">
								{t("publishing.category")}
								<ChipSelect
									multiSelect
									multiSelectValues={categories}
									onMultiSelectChange={(values) => setCategories(values.slice(0, 4))}
									disabled={!editor || busy}
									aria-label={t("publishing.category")}
									options={CATEGORIES.map((c) => ({ value: c, label: t(`publishing.category.${c}`) }))}
								/>
							</div>
							<div className="field">
								{t("publishing.language")}
								<ChipSelect
									multiSelect
									multiSelectValues={languages}
									onMultiSelectChange={setLanguages}
									placeholder={t("publishing.language.any")}
									disabled={!editor || busy}
									aria-label={t("publishing.language")}
									options={LANGUAGES.map((l) => ({ value: l, label: fmt.language(locale, l) }))}
								/>
							</div>
							{(["images", "video", "links"] as const).map((flag) => (
								<div className="field" key={flag}>
									{t(`distribution.${flag}`)}
									<ChipSelect
										value={flags[flag]}
										onChange={(value) => setFlags({ ...flags, [flag]: value })}
										disabled={!editor || busy}
										aria-label={t(`distribution.${flag}`)}
										options={["yes", "no", "restricted", "check"].map((v) => ({
											value: v,
											label: t(`distribution.${v}`),
										}))}
									/>
								</div>
							))}
							<label className="field" htmlFor="distribution-limit">
								{t("distribution.limit")}
								<Input
									id="distribution-limit"
									type="number"
									min={100}
									max={100000}
									value={limit}
									onChange={(e) => setLimit(e.target.value)}
								/>
							</label>
						</fieldset>
					</form>
				</ChipModalBody>
				<ChipModalFooter
					cancelLabel={t("common.cancel")}
					onCancel={() => setOpen(false)}
					primaryAction={{
						label: t("distribution.add"),
						type: "submit",
						form: "add-publishing-platform",
						disabled: !editor || busy,
					}}
				/>
			</ChipModal>
		</>
	);
}
